import { type AnimationClip, type AnimationAction } from 'three'
import { UI } from '../../UI'

export class AnimationPlayer {
  private readonly ui: UI
  private current_animation_clip: AnimationClip | null = null
  private current_animation_actions: AnimationAction[] = []
  private is_playing: boolean = false
  /// When the user grabs the scrubber, the animation is paused.
  /// If it was playing before the scrubber was grabbed, it will
  /// resume playing after the user lets go.
  private was_playing_before_user_scrubbed: boolean = false
  private is_user_scrubbing: boolean = false

  /**
   * Sorted list of keyframe times (seconds) for the active clip. Drives both the
   * visual markers on the scrubber and prev/next-keyframe jump targets. Empty if
   * the active clip is not user-authored.
   */
  private cached_keyframe_times: number[] = []
  private static readonly KEYFRAME_TIME_EPSILON = 1e-4

  private readonly has_added_event_listeners: boolean = false

  constructor () {
    this.ui = UI.getInstance()

    if (!this.has_added_event_listeners) {
      this.setup_event_listeners()
      this.has_added_event_listeners = true
    }
  }

  private setup_event_listeners (): void {
    // Play/Pause button
    if (this.ui.dom_play_pause_button !== null) {
      this.ui.dom_play_pause_button.addEventListener('click', () => {
        this.toggle_play_pause()
      })
    }

    // Animation scrubber
    if (this.ui.dom_animation_scrubber !== null) {
      this.ui.dom_animation_scrubber.addEventListener('input', (event) => {
        this.handle_scrubber_input(event)
      })

      this.ui.dom_animation_scrubber.addEventListener('mousedown', () => {
        this.was_playing_before_user_scrubbed = this.is_playing
        this.pause()
        this.is_user_scrubbing = true
      })

      this.ui.dom_animation_scrubber.addEventListener('mouseup', () => {
        this.is_user_scrubbing = false
        if (this.was_playing_before_user_scrubbed) {
          this.was_playing_before_user_scrubbed = false
          this.play()
        }
      })
    }

    // Click on a keyframe marker → seek to that time. Delegated on the markers
    // container so we don't re-bind on every render.
    if (this.ui.dom_keyframe_markers !== null) {
      this.ui.dom_keyframe_markers.addEventListener('click', (event) => {
        const target = event.target as HTMLElement | null
        const marker = target?.closest<HTMLElement>('.keyframe-marker')
        if (marker === null || marker === undefined) return
        const time_attr = marker.dataset.time
        if (time_attr === undefined) return
        const time = parseFloat(time_attr)
        if (!Number.isFinite(time)) return
        this.seek_to_time(time)
      })
    }
  }

  public animation_name_clean (input: string): string {
    return input.replace(/_/g, ' ')
  }

  public set_animation (animation_clip: AnimationClip, animation_actions: AnimationAction[]): void {
    this.current_animation_clip = animation_clip
    this.current_animation_actions = animation_actions

    // Calculate total frames (assuming 30 FPS)
    const fps = 30
    const total_frames = Math.floor(animation_clip.duration * fps)

    // Update UI
    if (this.ui.dom_current_animation_name !== null) {
      this.ui.dom_current_animation_name.textContent = this.animation_name_clean(animation_clip.name)
    }

    if (this.ui.dom_total_time !== null) {
      this.ui.dom_total_time.textContent = `${total_frames}`
    }

    // Enable controls
    if (this.ui.dom_play_pause_button !== null) {
      this.ui.dom_play_pause_button.disabled = false
    }

    if (this.ui.dom_animation_scrubber !== null) {
      this.ui.dom_animation_scrubber.disabled = false
      this.ui.dom_animation_scrubber.max = total_frames.toString()
      this.ui.dom_animation_scrubber.value = '0'
    }

    // Set initial playing state based on first animation action
    this.is_playing = animation_actions.length > 0 ? animation_actions[0].isRunning() : false
    this.update_play_pause_button()
  }

  public clear_animation (): void {
    this.current_animation_clip = null
    this.current_animation_actions = []
    this.is_playing = false
    this.clear_keyframe_markers()

    // Update UI
    if (this.ui.dom_current_animation_name !== null) {
      this.ui.dom_current_animation_name.textContent = 'No animation selected'
    }

    if (this.ui.dom_current_time !== null) {
      this.ui.dom_current_time.textContent = '0'
    }

    if (this.ui.dom_total_time !== null) {
      this.ui.dom_total_time.textContent = '0'
    }

    // Disable controls
    if (this.ui.dom_play_pause_button !== null) {
      this.ui.dom_play_pause_button.disabled = true
    }

    if (this.ui.dom_animation_scrubber !== null) {
      this.ui.dom_animation_scrubber.disabled = true
      this.ui.dom_animation_scrubber.value = '0'
    }

    this.update_play_pause_button()
  }

  public toggle_play_pause (): void {
    if (this.current_animation_actions.length === 0) {
      return
    }

    if (this.is_playing) {
      this.pause()
    } else {
      this.play()
    }
  }

  public play (): void {
    if (this.current_animation_actions.length === 0) {
      return
    }

    // Apply play/unpause to all animation actions
    this.current_animation_actions.forEach((action) => {
      if (action.paused === true) {
        action.paused = false
      } else {
        action.play()
      }
    })

    this.is_playing = true
    this.update_play_pause_button()
  }

  public pause (): void {
    if (this.current_animation_actions.length === 0) {
      return
    }

    // Apply pause to all animation actions
    this.current_animation_actions.forEach((action) => {
      action.paused = true
    })

    this.is_playing = false
    this.update_play_pause_button()
  }

  private update_play_pause_button (): void {
    if (this.ui.dom_play_pause_button === null) {
      return
    }

    const icon = this.ui.dom_play_pause_button.querySelector('.material-symbols-outlined')
    if (icon !== null) {
      icon.textContent = this.is_playing || this.was_playing_before_user_scrubbed ? 'pause' : 'play_arrow'
    }
  }

  private handle_scrubber_input (event: Event): void {
    if (this.current_animation_actions.length === 0 || this.current_animation_clip === null) {
      return
    }

    const target = event.target as HTMLInputElement
    const frame_number = parseFloat(target.value)

    // Convert frame to time (assuming 30 FPS)
    const fps = 30
    const scrub_time = frame_number / fps

    // Set the animation time for all actions
    this.current_animation_actions.forEach((action) => {
      action.time = scrub_time
    })

    // Update current frame display
    if (this.ui.dom_current_time !== null) {
      this.ui.dom_current_time.textContent = `${Math.round(frame_number)}`
    }
  }

  public update (_delta_time: number): void {
    if (this.current_animation_actions.length === 0 || this.current_animation_clip === null || this.is_user_scrubbing) {
      return
    }

    // Use the first action for time tracking (they should all be synchronized)
    const first_action = this.current_animation_actions[0]

    // Convert current time to frame number (assuming 30 FPS)
    const fps = 30
    const current_frame = first_action.time * fps

    // Update scrubber position based on frame number
    if (this.ui.dom_animation_scrubber !== null) {
      this.ui.dom_animation_scrubber.value = current_frame.toString()
    }

    // Update current frame display
    if (this.ui.dom_current_time !== null) {
      this.ui.dom_current_time.textContent = `${Math.round(current_frame)}`
    }

    // Check if animation has finished and loop it
    if (first_action.time >= this.current_animation_clip.duration) {
      this.current_animation_actions.forEach((action) => {
        action.time = 0
      })
    }
  }

  public get_is_playing (): boolean {
    return this.is_playing
  }

  /**
   * The current playhead time in seconds. Reads from the first action since all actions
   * are kept synchronized. Returns 0 when no animation is loaded.
   */
  public current_time (): number {
    if (this.current_animation_actions.length === 0) return 0
    return this.current_animation_actions[0].time
  }

  /**
   * Renders one visual marker per provided keyframe time on the scrubber. The
   * caller decides whether markers are appropriate for the active clip — the
   * player itself is agnostic of clip-source semantics. Pass an empty array
   * (or call `clear_keyframe_markers()`) to hide the markers.
   */
  public set_keyframe_markers (times: number[]): void {
    this.cached_keyframe_times = [...times].sort((a, b) => a - b)

    const container = this.ui.dom_keyframe_markers
    if (container === null) return
    container.innerHTML = ''

    const duration = this.current_animation_clip?.duration ?? 0
    if (duration <= 0) return

    for (const time of this.cached_keyframe_times) {
      // Clamp to [0, 1] so a stray beyond-duration keyframe doesn't render off-track.
      const fraction = Math.max(0, Math.min(1, time / duration))
      const marker = document.createElement('span')
      marker.className = 'keyframe-marker'
      marker.dataset.time = time.toString()
      marker.style.left = `${(fraction * 100).toFixed(3)}%`
      container.appendChild(marker)
    }
  }

  public clear_keyframe_markers (): void {
    this.cached_keyframe_times = []
    if (this.ui.dom_keyframe_markers !== null) {
      this.ui.dom_keyframe_markers.innerHTML = ''
    }
  }

  /** Returns the cached keyframe times. Used by callers to drive nav-button enable state. */
  public get_keyframe_times (): readonly number[] {
    return this.cached_keyframe_times
  }

  /**
   * Returns true if the playhead is within `KEYFRAME_TIME_EPSILON` of any cached
   * keyframe time. Used by the delete-keyframe button enable check.
   */
  public is_playhead_on_keyframe (): boolean {
    if (this.cached_keyframe_times.length === 0) return false
    const now = this.current_time()
    for (const t of this.cached_keyframe_times) {
      if (Math.abs(t - now) < AnimationPlayer.KEYFRAME_TIME_EPSILON) return true
    }
    return false
  }

  public jump_to_prev_keyframe (): void {
    if (this.cached_keyframe_times.length === 0) return
    const now = this.current_time()
    // strictly less, with epsilon so we don't get stuck on a keyframe we're already on
    let target: number | null = null
    for (let i = this.cached_keyframe_times.length - 1; i >= 0; i--) {
      const t = this.cached_keyframe_times[i]
      if (t < now - AnimationPlayer.KEYFRAME_TIME_EPSILON) { target = t; break }
    }
    // wrap to last if already at/before the first
    if (target === null) target = this.cached_keyframe_times[this.cached_keyframe_times.length - 1]
    this.seek_to_time(target)
  }

  public jump_to_next_keyframe (): void {
    if (this.cached_keyframe_times.length === 0) return
    const now = this.current_time()
    let target: number | null = null
    for (const t of this.cached_keyframe_times) {
      if (t > now + AnimationPlayer.KEYFRAME_TIME_EPSILON) { target = t; break }
    }
    // wrap to first if already at/past the last
    if (target === null) target = this.cached_keyframe_times[0]
    this.seek_to_time(target)
  }

  /**
   * Seeks the mixer + scrubber to the given time. Pauses playback so the user
   * sees the exact pose at that frame instead of immediately advancing.
   */
  private seek_to_time (time: number): void {
    if (this.current_animation_actions.length === 0 || this.current_animation_clip === null) return

    this.pause()

    this.current_animation_actions.forEach((action) => {
      action.time = time
    })

    const fps = 30
    const frame_number = time * fps

    if (this.ui.dom_animation_scrubber !== null) {
      this.ui.dom_animation_scrubber.value = frame_number.toString()
    }
    if (this.ui.dom_current_time !== null) {
      this.ui.dom_current_time.textContent = `${Math.round(frame_number)}`
    }

    // Fire a synthetic input event so step-listeners (e.g. delete-keyframe button
    // enable state) update without each caller having to do it.
    if (this.ui.dom_animation_scrubber !== null) {
      this.ui.dom_animation_scrubber.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }
}
